import React, {
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { ImageDropzone } from 'react-file-utils';

import ChatContext from '../../context/ChatContext';
import ChatChannelContext from '../../context/ChatChannelContext';
import MessageInputContext from '../../context/MessageInputContext';
import useToast from '../../hooks/useToast';
import useComponentMounted from '../../hooks/useComponentMounted';
import ChatDraftMessage from '../../Model/ChatDraftMessage';
import ChatMessage from '../../Model/ChatMessage';
import { ChannelType } from '../../Model/ChatChannel';
import MessageInput from '../MessageInput';
import { Event } from '../../utils/events';
import replaceLastWordWithEmoji from '../../utils/replaceLastWordWithEmoji';
import AttachmentsPreview from './components/AttachmentsPreview';
import ReplyMessagePreview from './components/ReplyMessagePreview';

import {
  Container,
  InputContainer,
} from './styles';
import texts from './texts.json';

const DRAFT_DEBOUNCE_MS = 1000;

const ChannelMessageInput = ({
  onOpenScheduler,
  onMessageSent,
  isMobileView,
  overrideText,
  additionalQuery,
  setSmartContextIsManual,
  pendingOutreachType,
  threadParent,
  threadNewestReplyAt,
}) => {
  const isComponentMountedRef = useComponentMounted();

  const [chatDraftMessageDoc, setChatDraftMessageDoc] = useState();
  const draftDebounce = useRef(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const lastAutoInsertedOverrideText = useRef(null);
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const hadDraftOnLoad = useRef(false);

  const [text, setText] = useState('');

  const {
    sendMessage,
    quotedMessage,
    setQuotedMessage,
    activeChannel,
    markThreadRead,
  } = useContext(ChatChannelContext);

  const {
    authUser: { id: userId },
    logEvent,
  } = useContext(ChatContext);

  const {
    attachments,
    onAddAttachments,
    onRemoveAttachment,
    reset,
    messageInputRef,
  } = useContext(MessageInputContext);

  const { showToast } = useToast();
  const currentChannelAttachments = attachments[activeChannel.id] || [];

  useEffect(() => {
    if (threadParent) {
      setIsDraftLoaded(true);
      return;
    }
    const init = async () => {
      setIsDraftLoaded(false);
      hadDraftOnLoad.current = false;
      const initChatDraftMessageDoc = await ChatDraftMessage.getMessageDoc(activeChannel.id, userId);
      if (isComponentMountedRef.current) {
        setChatDraftMessageDoc(initChatDraftMessageDoc);

        if (initChatDraftMessageDoc?.text && initChatDraftMessageDoc?.text !== '') {
          setText(initChatDraftMessageDoc.text);
          hadDraftOnLoad.current = true;
        }
        setIsDraftLoaded(true);
      }
    };
    if (activeChannel && activeChannel.id) {
      init();
    }
  }, [
    activeChannel,
    userId,
    isComponentMountedRef,
    setText,
    threadParent,
  ]);

  useEffect(() => {
    if (overrideText?.value?.trim()) {
      // If isManual is true, always replace the text (manual insert)
      if (overrideText.isManual) {
        setText(overrideText.value);
        setSmartContextIsManual(false); // Reset the manual flag to false to avoid auto-inserting the same text again
        lastAutoInsertedOverrideText.current = overrideText.value;
      } else {
        // For auto-insert: only insert if there was NO draft when we loaded
        // Wait until draft is loaded, then only insert once per overrideText value
        const hasNotAutoInsertedThisText = lastAutoInsertedOverrideText.current !== overrideText.value;
        if (isDraftLoaded && !hadDraftOnLoad.current && hasNotAutoInsertedThisText && !text.trim()) {
          setText(overrideText.value);
          lastAutoInsertedOverrideText.current = overrideText.value;
        }
      }
    }
  }, [overrideText, text, isDraftLoaded, setSmartContextIsManual]);

  const handleChange = (changedText) => {
    setText(changedText);
    if (threadParent) {
      return;
    }
    clearTimeout(draftDebounce.current);

    draftDebounce.current = setTimeout(async () => {
      await chatDraftMessageDoc?.setText(changedText);
    }, DRAFT_DEBOUNCE_MS);
  };

  const handleSubmit = async (newText) => {
    try {
      // Clear the debounce timeout first to prevent any pending saves
      clearTimeout(draftDebounce.current);
      draftDebounce.current = null;

      setIsSendingMessage(true);
      const textToSend = replaceLastWordWithEmoji((typeof newText === 'string') ? newText : text);
      /*
        Captured before the send: answering a thread is what marks it read, and the mark has to
        cover the replies that were already there rather than the one about to land. It comes from
        the thread's own reply query (`ThreadModal`) rather than the channel-wide inbox window — a
        thread outside that window has no row, and the mark would be silently dropped.
      */
      const threadReadUpTo = threadParent ? threadNewestReplyAt : 0;
      const data = { actionedBy: userId, channelId: activeChannel?.id || '' };
      logEvent(Event.BEFORE_SENDING_MESSAGE, data);
      const parent = {};
      if (threadParent) {
        parent.id = threadParent.id;
        parent.text = threadParent.text || '';
        parent.attachments = threadParent.attachments;
        parent.userId = threadParent.userId;
        if (threadParent.internalText) {
          parent.internalText = threadParent.internalText;
        }
      } else if (quotedMessage) {
        parent.id = quotedMessage.id;
        parent.text = quotedMessage.text || '';
        parent.attachments = quotedMessage.attachments;
        parent.userId = quotedMessage.userId;

        if (quotedMessage.internalText) {
          parent.internalText = quotedMessage.internalText;
        }
      }

      const isAiAugmented = overrideText?.source === 'smartContext';
      const augmentedText = overrideText?.value || '';
      const augmentedTextEdited = overrideText?.value !== textToSend;
      const smartContextTraceId = overrideText?.traceId || '';

      // Thread replies are text-only for this slice and must not touch the channel's shared
      // attachment bucket — sending currentChannelAttachments here would attach the main
      // composer's staged files to the thread reply.
      const attachmentsToSend = threadParent ? [] : currentChannelAttachments;

      await sendMessage(textToSend, attachmentsToSend, {
        ...((!!quotedMessage || threadParent) && {
          parent,
        }),
        ...(threadParent && activeChannel?.type === ChannelType.ADMIN_COACHING && {
          isThreadReply: true,
        }),
        isAiAugmented,
        ...(augmentedTextEdited && {
          augmentedText,
          augmentedTextEdited,
        }),
        ...(isAiAugmented && smartContextTraceId && {
          smartContextTraceId,
        }),
        additionalQuery,
        ...(pendingOutreachType && {
          outreachType: pendingOutreachType,
        }),
      });
      logEvent(Event.AFTER_SENDING_MESSAGE, data);
      setText('');
      if (threadParent) {
        // Answering a thread is what resolves it — the coach acted, so it leaves the unread inbox.
        markThreadRead(threadParent.id, threadReadUpTo);
      } else {
        setQuotedMessage();
        chatDraftMessageDoc?.setText('');
        // reset() clears the channel's shared attachment bucket — skip it for thread
        // replies so the main composer's staged files are preserved.
        reset();
      }
      onMessageSent(textToSend);
    } catch (error) {
      showToast(`${texts.sendMessageError}: ${error.message}`, { error: true });
    } finally {
      setIsSendingMessage(false);
      messageInputRef.current?.focus();
    }
  };

  const messageInput = (
    <MessageInput
      text={text}
      onChange={handleChange}
      onSubmit={handleSubmit}
      onAddAttachments={onAddAttachments}
      onClickSchedule={onOpenScheduler}
      isDisabled={text.trim().length === 0 && (!!threadParent || currentChannelAttachments.length === 0)}
      isSendingMessage={isSendingMessage}
      inputRef={messageInputRef}
      isMobileView={isMobileView}
      hideAttachments={!!threadParent}
      hideVoiceRecording={!!threadParent}
    />
  );

  return (
    <InputContainer>
      {quotedMessage && (
        <ReplyMessagePreview
          quotedMessage={quotedMessage}
          setQuotedMessage={setQuotedMessage}
        />
      )}
      <Container>
        {threadParent ? messageInput : (
          <ImageDropzone
            handleFiles={onAddAttachments}
            multiple
          >
            <AttachmentsPreview
              attachments={currentChannelAttachments}
              onRemove={onRemoveAttachment}
              onAddAttachments={onAddAttachments}
            />
            {messageInput}
          </ImageDropzone>
        )}
      </Container>
    </InputContainer>
  );
};

ChannelMessageInput.propTypes = {
  onOpenScheduler: PropTypes.func,
  onMessageSent: PropTypes.func,
  isMobileView: PropTypes.bool,
  overrideText: PropTypes.shape({
    value: PropTypes.string,
    source: PropTypes.string,
    isManual: PropTypes.bool,
    traceId: PropTypes.string,
  }),
  additionalQuery: PropTypes.string,
  setSmartContextIsManual: PropTypes.func,
  pendingOutreachType: PropTypes.string,
  threadParent: PropTypes.instanceOf(ChatMessage),
  threadNewestReplyAt: PropTypes.number,
};

ChannelMessageInput.defaultProps = {
  onOpenScheduler: undefined,
  onMessageSent: () => {},
  isMobileView: false,
  overrideText: { value: '', source: '', isManual: false },
  additionalQuery: '',
  setSmartContextIsManual: () => {},
  pendingOutreachType: null,
  threadParent: null,
  threadNewestReplyAt: 0,
};

export default ChannelMessageInput;
